<template>
    <div>
        <label :for="name" class="block text-sm font-medium text-gray-700" v-if="label">
            {{ label }} <span v-if="required">*</span>
        </label>
        <div :class="{'mt-1': label}">
            <input
                :type="type"
                :name="name"
                :id="name"
                :autocomplete="autocomplete"
                :placeholder="placeholder"
                :class="classComputed"
                v-bind:value="modelValue"
                v-on:input="onInput"
                v-on:keydown="onKeydown"
                :tabindex="tabindex"
                :disabled="disabled"
                :required="required"
                :inputmode="inputmode"
            >
        </div>
        <p
            v-if="hint"
            class="mt-1 text-xs"
            :class="error ? 'text-red-600' : 'text-gray-500'"
        >{{ hint }}</p>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: String,
  name: String,
  id: String,
  modelValue: [String, Number],
  placeholder: String,
  autocomplete: String,
  tabindex: Number,
  inputmode: String,
  // small helper text below the input, e.g. "Nur ganze Zahlen"
  hint: String,
  // only accept digits and the decimal separators , and .
  numericOnly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'text',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue']);

// everything a price may consist of, the separators are kept so an amount
// like "555,12" can still be flagged as invalid instead of silently changing
const allowedNumericChars = /[0-9,.]/;

const onKeydown = (event) => {
  if (!props.numericOnly) return;

  // don't get in the way of shortcuts like copy & paste
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  // keys like Backspace, Tab or the arrow keys report more than one character
  if (event.key.length !== 1) return;

  if (!allowedNumericChars.test(event.key)) {
    event.preventDefault();
  }
};

const onInput = (event) => {
  if (!props.numericOnly) {
    emit('update:modelValue', event.target.value);
    return;
  }

  // strip letters that arrived by pasting or dropping text
  const cleaned = event.target.value.replace(/[^0-9,.]/g, '');

  if (event.target.value !== cleaned) {
    event.target.value = cleaned;
  }

  emit('update:modelValue', cleaned);
};

const classComputed = computed(() => {
  return {
    'shadow-sm': true,
    'block': true,
    'w-full': true,
    'sm:text-sm': true,
    'rounded-md': true,

    'border-gray-300': !props.error,
    'focus:ring-primary-500': !props.error,
    'focus:border-primary-500': !props.error,

    'border-red-500': props.error,
    'focus:ring-red-500': props.error,
    'focus:border-red-500': props.error,

    'bg-gray-100': props.disabled,
    'text-gray-400': props.disabled,
  }
});
</script>
